import { BlockType, DataSourceType } from '@lighthouse/core'
import type { FindUseADTObject, FindUseLocationConfig } from '@lighthouse/shared'
import { BlockTypeIconMap } from '@lighthouse/shared'
import {
    FindUseLocationType,
    getFieldIcon,
    getTableIcon,
    nodeTypeIconMap,
    TRIGGER_NODES,
    useAtomAction,
    useAtomData
} from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import { useAtomCallback } from 'jotai/utils'
import { find } from 'rambda'
import React, { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router'
import styled from 'styled-components'

import { setFindUseAtom } from '@/atoms/application/action'
import { syncComponentsAtom } from '@/atoms/application/state'
import { initContainerRuntimeStateAtom, openPageStackAtom, setPageInternalStateAtom } from '@/atoms/page/action'
import { pageBlocksAtom, pageInternalStateAtomFamily, pageListAtom, pageStackAtom, pageStackHistoriesAtom } from '@/atoms/page/state'
import { AsideType } from '@/atoms/page/types'
import { stackFactory } from '@/atoms/page/utils'
import { useCurrentAppId } from '@/hooks/useApplication'
import { useDataSourceEnvId, useDataSourceList } from '@/hooks/useDataSource'

import { FindUseLocationItem } from './FindUseLocationItem'

const SCxList = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
`

interface FindUseLocationItemProps {
    config: FindUseADTObject
    data: FindUseLocationConfig[]
}

export const FindUseLocationList: React.FC<FindUseLocationItemProps> = ({ config, data }) => {
    const navigate = useNavigate()
    const appId = useCurrentAppId()
    const envId = useDataSourceEnvId()

    const pageList = useAtomData(pageListAtom)
    const dataSourceList = useDataSourceList(appId, envId)
    const { run: setFindUse } = useAtomAction(setFindUseAtom)
    const { run: openPageStack } = useAtomAction(openPageStackAtom)
    const { run: setPageStack } = useAtomAction(pageStackAtom)
    const { run: setPageInternalState } = useAtomAction(setPageInternalStateAtom)
    const { run: setPageStackIndexHistories } = useAtomAction(pageStackHistoriesAtom)
    const { run: initContainerRuntimeState } = useAtomAction(initContainerRuntimeStateAtom)

    const getBlockIconType = useCallback((type: BlockType) => {
        switch (type) {
            case BlockType.container: {
                return ''
            }
            case BlockType.field: {
                return 'LayerFormContainerBlock'
            }
            case BlockType.chart: {
                return 'DiagramUpBlock'
            }
            case BlockType.view: {
                return 'NewTableBlock'
            }
            default: {
                return BlockTypeIconMap[type]
            }
        }
    }, [])

    const handleLink = useAtomCallback((get, _, val: FindUseLocationConfig) => {
        switch (val.type) {
            case FindUseLocationType.DATASHEET: {
                const { type, ...datasheet } = val
                const { dsId } = datasheet

                const ds = find(item => item.id === dsId, dataSourceList)
                if (!ds) {
                    return
                }
                const { type: dsType } = ds
                setFindUse({
                    config,
                    location: val
                })
                if (dsType === DataSourceType.joinDataSource) {
                    navigate({ pathname: `/${appId}/dataSource/${dsId}` })
                    return
                }
                navigate({ pathname: `/${appId}/aggregate/${dsId}` })
                return
            }

            case FindUseLocationType.FIELD: {
                const { type, ...field } = val
                const { dsId } = field
                setFindUse({
                    config,
                    location: val
                })
                navigate({ pathname: `/${appId}/dataSource/${dsId}` })
                return
            }

            case FindUseLocationType.PAGE: {
                const { type, ...page } = val
                const { pageId } = page
                setFindUse({
                    config,
                    location: val
                })
                const stackId = nanoid()
                openPageStack(
                    stackFactory({
                        pageId,
                        stackId
                    })
                )
                navigate(`/${appId}/page`)
                return
            }

            case FindUseLocationType.WORKFLOW: {
                const { type, ...workflow } = val
                const { workflowId } = workflow
                setFindUse({
                    config,
                    location: val
                })
                navigate({ pathname: `/${appId}/flow/${workflowId}` })
                return
            }
            // block
            default: {
                const { type, ...block } = val
                const { pageId, dsId, blockId } = block
                const page = find(item => item.id === pageId, pageList)
                if (!page) {
                    return
                }
                const blocks = get(pageBlocksAtom(pageId))
                const syncComponents = get(syncComponentsAtom)
                const stackId = nanoid()
                setFindUse({
                    config,
                    location: val
                })
                setPageStack(stackFactory({ stackId, pageId }))
                setPageStackIndexHistories([stackId])
                initContainerRuntimeState({ stackId, blocks, syncComponents })
                // 设置选中状态
                setPageInternalState({
                    stackId,
                    state: {
                        selectedNodes: [{ id: blockId }],
                        asideType: AsideType.BLOCK
                    }
                })

                navigate(`/${appId}/page`)
            }
        }
    })

    const getListData = useCallback(
        (data: FindUseLocationConfig) => {
            switch (data.type) {
                case FindUseLocationType.DATASHEET: {
                    return {
                        key: data.dsId,
                        title: data.dsName,
                        description: data.nodeName,
                        icon: getTableIcon({ type: data.dsType, sync: false })
                    }
                }

                case FindUseLocationType.FIELD: {
                    return {
                        key: data.fieldId,
                        title: data.fieldName,
                        description: `数据表：${data.dsName}`,
                        icon: getFieldIcon(data.fieldId, data.fieldType)
                    }
                }

                case FindUseLocationType.PAGE: {
                    return {
                        key: data.pageId,
                        title: data.pageName,
                        icon: 'PageBroadContent'
                    }
                }

                case FindUseLocationType.WORKFLOW: {
                    const icon = nodeTypeIconMap[data.nodeType]
                    const isTriggerNode = TRIGGER_NODES.has(data.nodeType)
                    const workFlowTypeText = data.workflowType === 'automation' ? '自动化' : '动作流'
                    return {
                        key: data.nodeId,
                        isWorkFlow: true,
                        title: data.nodeName,
                        isTriggerNode,
                        description: `${workFlowTypeText}：${data.workflowName}`,
                        icon
                    }
                }

                default: {
                    const icon = getBlockIconType(data.blockType)
                    return {
                        key: data.blockId,
                        title: data.blockName,
                        description: `页面：${data.pageName}`,
                        icon
                    }
                }
            }
        },
        [getBlockIconType]
    )

    const list = useMemo(() => {
        return data.map(item => {
            const { key, title, description, icon, isTriggerNode, isWorkFlow } = getListData(item)

            return (
                <FindUseLocationItem
                    key={key}
                    title={title}
                    description={description}
                    icon={icon}
                    isWorkFlow={isWorkFlow}
                    isTriggerNode={isTriggerNode}
                    onLink={() => handleLink(item)}
                />
            )
        })
    }, [data, getListData, handleLink])

    return <SCxList>{list}</SCxList>
}
